import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

/* 삭제하지 말 것 !!!
환불 프로세스 흐름(⚠️ 부분만 자동화 - 관리자 승인 필요):

  1단계: 사용자 구독 취소 (자동)
    - 14일 이내 + 미사용 → P_refund_requests 테이블에 'pending' 상태로 자동 생성
    - LemonSqueezy 구독은 즉시 취소됨

  2단계: 관리자 승인 (수동)
    - 관리자가 RefundManagement.tsx에서 환불 요청 확인
    - "승인" 버튼 클릭 시:
      // "refund-process" Edge Function 호출
    - LemonSqueezy API로 환불 처리 (orders/refunds POST)
    - DB 상태를 'approved'로 업데이트

  3단계: 환불 처리 (자동)
    - LemonSqueezy가 실제 환불 처리 (1-3 영업일)
    - 고객 카드로 자동 환불

핵심 포인트

  ✅ 구독 취소는 완전 자동화
    - 환불 자격 여부와 관계없이 LemonSqueezy API 호출

  ⚠️ 환불은 반자동화
    - 환불 요청은 자동 생성
    - 관리자 승인 필요 (RefundManagement.tsx)
    - 승인 후 LemonSqueezy가 자동 처리

  보안 및 컴플라이언스
    - 14일 환불 정책 자동 체크
    - 사용량 추적으로 남용 방지
    - 관리자 검토로 부정 환불 차단



📧 사용자에게 이메일 발송 케이스

  1. 승인 (approved) - 환불 승인됨

  - 트리거: 관리자가 환불 요청을 승인할 때
  - 이메일 내용:
    - 제목: "Refund Request Update - approved"
    - 승인 안내 메시지
    - 환불 금액 정보
    - 처리 시간: 5-10 영업일 안내
    - 원결제 수단으로 환불 예정 안내

  2. 거부 (rejected) - 환불 거부됨

  - 트리거: 관리자가 환불 요청을 거부할 때
  - 이메일 내용:
    - 제목: "Refund Request Update - rejected"
    - 거부 안내 메시지
    - 관리자 메모 (admin_notes) 포함
    - 이의 제기 안내 (support@aivuebiz.com)

  3. 완료 (completed) - 환불 완료됨

  - 트리거: LemonSqueezy에서 실제 환불이 처리 완료될 때
  - 이메일 내용:
    - 제목: "Refund Request Update - completed"
    - 환불 완료 안내
    - 환불 금액 및 거래 ID
    - 입금 예상 시간: 3-5 영업일

  🔧 시스템 구조

  1. SQL 트리거 (notify_refund_request())
    - D_refund_requests 테이블의 status 변경 감지
    - 상태가 approved, rejected, completed로 변경 시 pg_notify 발생
  2. Edge Function (refund-notify)
    - notification_type이 status_update일 때 실행
    - 각 상태에 따른 이메일 템플릿 생성
    - Resend API를 통한 실제 이메일 발송
  3. Fallback 메커니즘
    - Resend API 실패 시 D_email_queue 테이블에 저장
    - 나중에 수동 또는 배치로 처리 가능

  ⚠️ 주의사항
  - 새 환불 요청 시: 관리자에게만 알림 (support@aivuebiz.com)
  - 사용자 이메일: 상태 변경 시에만 발송 (pending 상태에서는 발송 안 함)
  - 이메일 서비스: RESEND_API_KEY 설정 필수
*/

interface RefundRequest {
  refund_request_id: string;
  user_id: string;
  subscription_id: string;
  refund_amount: number;
  reason: string;
  refund_status: 'pending' | 'approved' | 'rejected' | 'processing';
  admin_notes: string | null;
  processed_by: string | null;
  processed_at: string | null;
  lemonsqueezy_refund_id: string | null;
  refund_requested_at: string;
  refund_updated_at: string;
  
  // User info
  user_email: string;
  user_name: string | null;
  user_phone: string | null;
  
  // Subscription info
  plan_id: string;
  subscription_status: string;
  current_period_start: string;
  current_period_end: string;
  lemonsqueezy_subscription_id: string;
  lemonsqueezy_order_id: string;
  subscription_created_at: string;
  
  // Plan info at request time
  plan_name: string;
  plan_price: number;
  billing_interval: string;
  
  // Current plan info (may be different from request time)
  current_plan_name: string | null;
  current_plan_price: number | null;
  current_billing_interval: string | null;
  current_subscription_status: string | null;
  current_period_start_now: string | null;
  current_period_end_now: string | null;
  
  // Plan comparison indicators
  plan_changed_since_request: boolean;
  subscription_status_changed: boolean;
  
  // Eligibility
  within_refund_period: boolean;
  days_since_subscription: number;
  usage_summary: {
    messages_sent: number;
    video_minutes: number;
    stt_characters: number;
    file_size_mb: number;
    has_usage: boolean;
  };
  
  // Admin info
  processed_by_email: string | null;
  processed_by_name: string | null;
}

interface RefundManagementProps {
  supabaseUrl: string;
  supabaseAnonKey: string;
  isKorean: boolean;
  userRole?: 'admin' | 'super_admin' | null;
}

const RefundManagement: React.FC<RefundManagementProps> = ({ supabaseUrl, supabaseAnonKey, isKorean, userRole }) => {
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<{ [key: string]: string }>({});

  const supabase = createClient(supabaseUrl, supabaseAnonKey);

  useEffect(() => {
    fetchRefundRequests();
  }, []);

  const fetchRefundRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase.rpc('get_admin_refund_requests');
      
      if (error) {
        throw error;
      }
      
      setRefundRequests(data || []);
    } catch (err: any) {
      console.error('Error fetching refund requests:', err);
      setError(err.message || 'Failed to fetch refund requests');
    } finally {
      setLoading(false);
    }
  };

  const processRefund = async (requestId: string, action: 'approve' | 'reject') => {
    try {
      setProcessingId(requestId);
      setError(null);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      // Get the correct function URL based on environment
      const isDev = window.location.hostname === 'localhost' || window.location.hostname.includes('dev');
      const functionUrl = isDev 
        ? `${supabaseUrl}/functions/v1/refund-process`
        : `${supabaseUrl}/functions/v1/refund-process`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          refund_request_id: requestId,
          action: action,
          admin_notes: adminNotes[requestId] || `${action === 'approve' ? 'Approved' : 'Rejected'} by admin`,
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to process refund');
      }
      
      // Refresh the list
      await fetchRefundRequests();
      
      // Clear admin notes for this request
      setAdminNotes(prev => {
        const newNotes = { ...prev };
        delete newNotes[requestId];
        return newNotes;
      });
      
      alert(isKorean ? `환불이 ${action === 'approve' ? '승인' : '거부'}되었습니다.` : `Refund ${action}d successfully`);
    } catch (err: any) {
      console.error('Error processing refund:', err);
      setError(err.message || 'Failed to process refund');
      alert(isKorean ? `오류: ${err.message}` : `Error: ${err.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isKorean ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(isKorean ? 'ko-KR' : 'en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    
    const statusText = {
      pending: isKorean ? '대기중' : 'Pending',
      processing: isKorean ? '처리중' : 'Processing',
      approved: isKorean ? '승인됨' : 'Approved',
      rejected: isKorean ? '거부됨' : 'Rejected',
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status as keyof typeof statusColors]}`}>
        {statusText[status as keyof typeof statusText]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6">
        {isKorean ? '환불 요청 관리' : 'Refund Request Management'}
      </h2>
      
      {refundRequests.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          {isKorean ? '환불 요청이 없습니다.' : 'No refund requests found.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '신청자' : 'Applicant'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '구독 정보 (신청시/현재)' : 'Subscription (Request/Current)'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '사용량' : 'Usage'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '환불 자격' : 'Eligibility'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '상태' : 'Status'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {isKorean ? '작업' : 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {refundRequests.map((request) => (
                <tr key={request.refund_request_id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{request.user_email}</div>
                      <div className="text-gray-500">{request.user_name || '-'}</div>
                      <div className="text-xs text-gray-400">
                        {isKorean ? '신청일' : 'Requested'}: {formatDate(request.refund_requested_at)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {/* Plan at request time */}
                      <div className="font-medium text-gray-900">
                        <span className="text-blue-600">{isKorean ? '신청시' : 'At Request'}: </span>
                        {request.plan_name} ({formatCurrency(request.plan_price)}/{request.billing_interval})
                      </div>
                      
                      {/* Current plan (if different) */}
                      {request.plan_changed_since_request && request.current_plan_name && (
                        <div className="font-medium text-orange-600 mt-1" 
                             title={isKorean ? 
                               `계획이 변경되었습니다: ${request.plan_name} → ${request.current_plan_name}` : 
                               `Plan changed: ${request.plan_name} → ${request.current_plan_name}`}>
                          <span className="text-orange-600">{isKorean ? '현재' : 'Current'}: </span>
                          {request.current_plan_name} ({formatCurrency(request.current_plan_price || 0)}/{request.current_billing_interval})
                          <span className="ml-2 px-1 py-0.5 bg-orange-100 text-orange-800 text-xs rounded cursor-help">
                            ⚠️ {isKorean ? '변경됨' : 'Changed'}
                          </span>
                        </div>
                      )}
                      
                      {/* Show current plan even if same (for clarity) */}
                      {!request.plan_changed_since_request && request.current_plan_name && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ {isKorean ? '플랜 변경 없음' : 'Plan unchanged'}
                        </div>
                      )}
                      
                      {/* Subscription status change indicator */}
                      {request.subscription_status_changed && (
                        <div className="text-xs text-purple-600 mt-1"
                             title={isKorean ? 
                               `구독상태 변경: ${request.subscription_status} → ${request.current_subscription_status}` : 
                               `Status changed: ${request.subscription_status} → ${request.current_subscription_status}`}>
                          <span className="px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded cursor-help">
                            📊 {isKorean ? '구독상태 변경' : 'Status Changed'}
                          </span>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 mt-1">
                        {isKorean ? '구독일' : 'Subscribed'}: {formatDate(request.subscription_created_at)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {isKorean ? '환불액' : 'Refund'}: {formatCurrency(request.refund_amount)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {request.usage_summary.has_usage ? (
                        <div>
                          <span className="text-red-600 font-medium">
                            {isKorean ? '사용함' : 'Used'}
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {request.usage_summary.messages_sent > 0 && (
                              <div>{isKorean ? '메시지' : 'Messages'}: {request.usage_summary.messages_sent}</div>
                            )}
                            {request.usage_summary.video_minutes > 0 && (
                              <div>{isKorean ? '영상통화' : 'Video'}: {request.usage_summary.video_minutes}분</div>
                            )}
                            {request.usage_summary.stt_characters > 0 && (
                              <div>STT: {request.usage_summary.stt_characters}</div>
                            )}
                            {request.usage_summary.file_size_mb > 0 && (
                              <div>{isKorean ? '파일' : 'Files'}: {request.usage_summary.file_size_mb}MB</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-green-600 font-medium">
                          {isKorean ? '미사용' : 'Unused'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {request.within_refund_period ? (
                        <div>
                          <span className="text-green-600 font-medium">
                            {isKorean ? '기간 내' : 'Within Period'}
                          </span>
                          <div className="text-xs text-gray-500">
                            {request.days_since_subscription}{isKorean ? '일 경과' : ' days'}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="text-red-600 font-medium">
                            {isKorean ? '기간 초과' : 'Period Expired'}
                          </span>
                          <div className="text-xs text-gray-500">
                            {request.days_since_subscription}{isKorean ? '일 경과' : ' days'}
                          </div>
                        </div>
                      )}
                      {!request.usage_summary.has_usage && request.within_refund_period && (
                        <div className="mt-1">
                          <span className="text-xs text-green-600 font-bold">
                            ✓ {isKorean ? '환불 가능' : 'Eligible'}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {getStatusBadge(request.refund_status)}
                    {request.processed_by_email && (
                      <div className="text-xs text-gray-500 mt-1">
                        {request.processed_by_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {request.refund_status === 'pending' ? (
                      <div className="space-y-2">
                        <div>
                          <input
                            type="text"
                            placeholder={isKorean ? '관리자 메모' : 'Admin notes'}
                            value={adminNotes[request.refund_request_id] || ''}
                            onChange={(e) => setAdminNotes(prev => ({
                              ...prev,
                              [request.refund_request_id]: e.target.value
                            }))}
                            className="w-full px-2 py-1 text-xs border rounded"
                          />
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              if (userRole !== 'super_admin') {
                                alert(isKorean ? 'super_admin 권한이 필요합니다.' : 'super_admin permission required.');
                                return;
                              }
                              processRefund(request.refund_request_id, 'approve');
                            }}
                            disabled={processingId === request.refund_request_id || userRole !== 'super_admin'}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            title={userRole !== 'super_admin' ? (isKorean ? 'super_admin 권한 필요' : 'super_admin permission required') : ''}
                          >
                            {processingId === request.refund_request_id ? '...' : (isKorean ? '승인' : 'Approve')}
                          </button>
                          <button
                            onClick={() => {
                              if (userRole !== 'super_admin') {
                                alert(isKorean ? 'super_admin 권한이 필요합니다.' : 'super_admin permission required.');
                                return;
                              }
                              processRefund(request.refund_request_id, 'reject');
                            }}
                            disabled={processingId === request.refund_request_id || userRole !== 'super_admin'}
                            className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                            title={userRole !== 'super_admin' ? (isKorean ? 'super_admin 권한 필요' : 'super_admin permission required') : ''}
                          >
                            {processingId === request.refund_request_id ? '...' : (isKorean ? '거부' : 'Reject')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">
                        {request.admin_notes && (
                          <div className="text-xs">
                            {request.admin_notes}
                          </div>
                        )}
                        {request.lemonsqueezy_refund_id && (
                          <div className="text-xs text-blue-600">
                            LemonSqueezy: {request.lemonsqueezy_refund_id}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-4 flex justify-end">
        <button
          onClick={fetchRefundRequests}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {isKorean ? '새로고침' : 'Refresh'}
        </button>
      </div>
    </div>
  );
};

export default RefundManagement;